import { isEmpty, isNil } from 'lodash';
import { getters } from '@/modules/core/app/helpers/store';
import { FontSize } from '@/modules/ta/reportstudio/reportstudio.constants';
import { DataSourceType } from '@/modules/core/app/constants/datasource.constants';
import { ClientFields } from '@/modules/ta/widget/builder.constants';

// eslint-disable-next-line tap/no-raw-text-js
const FILTER_MODE_WIDGET = 'widget_filters';
// eslint-disable-next-line tap/no-raw-text-js
const CLIENT_ID_PARAM = 'client_id';
// eslint-disable-next-line tap/no-raw-text-js
const CLIENT_GROUP_ID_PARAM = 'client_group_id';
// eslint-disable-next-line tap/no-raw-text-js
const CLUSTER_ID_PARAM = 'cluster_id';

export class WidgetContextService {
  /**
   * @returns {boolean}
   */
  static isDashboardContext() {
    return !!getters.dashboard.getDashboard();
  }

  /**
   * @returns {boolean}
   */
  static isReportStudioContext() {
    return !!getters.reportstudio.getReport();
  }

  static isGlobalFilterEnabled() {
    return (
      this.isReportStudioContext() &&
      !(getters.reportstudio.getReport().metadata.filter_mode === FILTER_MODE_WIDGET)
    );
  }

  /**
   * @returns {SelectedEntity|null}
   */
  static getSelectedEntity() {
    return this.isDashboardContext()
      ? getters.dashboard.getSelectedEntity()
      : getters.reportstudio.getSelectedEntity();
  }

  /**
   * @returns {DashboardFilterItem}
   */
  static getCurrentActiveFilter() {
    return this.isDashboardContext() ? getters.dashboard.getCurrentActiveFilter() : null;
  }

  static getClientFilter() {
    if (getters.dashboard.isClientFilter()) {
      return this.getCurrentActiveFilter();
    }
    return this.getSelectedEntity();
  }

  /**
   * @returns {boolean|}
   */
  static isViewingAsClient() {
    const selectedEntity = this.getSelectedEntity();
    const isClientEntity =
      selectedEntity &&
      (selectedEntity.type === DataSourceType.CLIENT || selectedEntity.type === 'client_id');
    if (!isEmpty(selectedEntity) && isClientEntity) {
      return true;
    }

    if (this.isDashboardContext() && getters.export.isExporting()) {
      return (
        !isNil(getters.export.getConfig().reportVars.report_descriptor) ||
        (!isEmpty(selectedEntity) && isClientEntity)
      );
    }
    return getters.dashboard.isClientFilter();
  }

  static getParentId() {
    return getters.dashboard.getDashboard()?.id || getters.reportstudio.getReport()?.id;
  }

  /**
   * @returns {boolean|*}
   */
  static isDemoModeEnabled() {
    return (
      (this.isDashboardContext() && getters.dashboard.getDashboard().isDemoModeEnabled()) ||
      (this.isReportStudioContext() && getters.reportstudio.getReport().isDemoModeEnabled())
    );
  }

  /**
   *
   * @returns {number}
   */
  static getFontSize() {
    const context = this.isDashboardContext()
      ? getters.dashboard.getDashboard()
      : getters.reportstudio.getReport();
    const metadata = context?.metadata || {};
    switch (metadata?.font_size) {
      case FontSize.SIZE_SMALL:
        return 12;
      case FontSize.SIZE_MEDIUM:
        return 16;
      case FontSize.SIZE_LARGE:
        return 20;

      default:
        return 14;
    }
  }

  static resolveSelectedEntityIdForType(selectedType = DataSourceType.CLIENT) {
    let id = 0;
    let type = null;

    if (this.isDashboardContext()) {
      const currentPage = getters.dashboard.getDashboard();
      const pageEntity = currentPage.metadata ? currentPage.metadata.selected_entity : null;
      ({ type = null, id = 0 } = pageEntity || {});
    } else if (this.isReportStudioContext() && selectedType === DataSourceType.CLIENT) {
      id = getters.reportstudio.getReport().client_id ?? 0;
      type = id ? selectedType : null;
    }

    if (type === selectedType && id) {
      return Number(id);
    }

    return 0;
  }

  static resolvePageEntityQueryParam() {
    if (this.isDashboardContext()) {
      const selectedEntity = this.getSelectedEntity();
      const activeFilter = getters.dashboard.getCurrentActiveFilter();
      if (!isEmpty(selectedEntity)) {
        return selectedEntity?.type === DataSourceType.CLIENT_GROUP
          ? { [CLIENT_GROUP_ID_PARAM]: selectedEntity?.id }
          : { [CLIENT_ID_PARAM]: selectedEntity?.id };
      }
      if (getters.dashboard.isClientFilter()) {
        return { [CLIENT_ID_PARAM]: activeFilter?.id };
      }
      if (getters.dashboard.isClientGroupFilter()) {
        return { [CLIENT_GROUP_ID_PARAM]: activeFilter?.id };
      }
      if (getters.dashboard.isClusterFilter()) {
        return { [CLUSTER_ID_PARAM]: activeFilter?.id };
      }
    } else if (this.isReportStudioContext()) {
      const selectedEntity = this.getSelectedEntity();
      if (!isEmpty(selectedEntity)) {
        const { id, type } = selectedEntity;
        if (type !== `${DataSourceType.SMART_CAMPAIGN}_id`) {
          return { [type]: id };
        }
      }
    }

    return {};
  }

  static getDashFilterAlreadyExists(currentWidget, currentFilter) {
    let alreadyExists = false;
    const layouts = getters.dashboard.getOrderedLayoutsWithHydratedWidgets();

    layouts.some((layout) =>
      Object.values(layout.widgets).some((widget) => {
        if (alreadyExists || currentWidget.has_live_integration !== widget.has_live_integration) {
          return true;
        }

        const effectiveWidget =
          widget.id === currentWidget.id
            ? {
                ...widget,
                metadata: {
                  ...widget.metadata,
                  dynamic: {
                    ...widget.metadata.dynamic,
                    filters: widget.metadata.dynamic.filters.map((filter) =>
                      filter.field === currentFilter.field ? currentFilter : filter
                    ),
                  },
                },
              }
            : widget;

        if (currentWidget.id === effectiveWidget.id && currentFilter.expose_as_dash_filter) {
          return true;
        }

        const filterDataSource = currentWidget.metadata.data_source;
        const widgetDataSource = effectiveWidget.metadata.data_source;

        // For goals there is no defined datasource and id should be null
        if (filterDataSource.isGoalDataSourceType() && !isNil(filterDataSource.id)) {
          filterDataSource.id = null;
        }

        if (this.isFilterCompatibleWithWidgetDataSource(widgetDataSource, filterDataSource)) {
          alreadyExists = effectiveWidget.metadata.dynamic.filters.some(
            (filter) => filter.field === currentFilter.field && filter.expose_as_dash_filter
          );
        }

        return alreadyExists;
      })
    );

    return alreadyExists;
  }

  /**
   * @param widgetDataSource
   * @param filterDataSource
   * @returns {boolean}
   */
  static isFilterCompatibleWithWidgetDataSource(widgetDataSource, filterDataSource) {
    return (
      widgetDataSource &&
      filterDataSource &&
      (((filterDataSource.type === DataSourceType.GOAL_DATA ||
        filterDataSource.type === DataSourceType.BENCHMARK_DATA) &&
        widgetDataSource.type === filterDataSource.type) ||
        (widgetDataSource.type === filterDataSource.type &&
          widgetDataSource.id === filterDataSource.id &&
          widgetDataSource.data_view === filterDataSource.data_view))
    );
  }

  /**
   * @param filter
   * @returns {boolean}
   */
  static isClientFilter(filter) {
    return (
      filter.label === ClientFields.CLIENT ||
      filter.label === ClientFields.CLIENT_ID ||
      filter.label === ClientFields.CLIENT_BILLING_ID ||
      filter.field === ClientFields.CLIENT_FIELD_ID ||
      filter.field === ClientFields.CUSTOMER_ID
    );
  }
}
